import { Color, Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon, CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { RefObject, useEffect, useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { PoliticallyExposedPersonImage } from 'dashboard/models/report/politically-exposed-person';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FontWeight } from 'design-system/variables/text';
import { useBoundingClientRect } from 'lib/hooks/useBoundingClientRect';
import useCounter from 'lib/hooks/useCounter';
import { useIsOverflow } from 'lib/hooks/useIsOverflow';

export interface PoliticallyExposedPersonPhotoComparisonProps {
  referenceImages: PoliticallyExposedPersonImage[];
  profileImageUrl?: string;
}

export const PoliticallyExposedPersonPhotoComparison = ({
  referenceImages,
  profileImageUrl,
}: PoliticallyExposedPersonPhotoComparisonProps) => {
  const [imageIndex, { set: setImageIndex }] = useCounter(0, {
    mod: referenceImages?.length ?? 1,
  });

  const currentReferenceImage = referenceImages[imageIndex];
  const [photoThumbnailsBoundingRect, photoThumbnailsRef] = useBoundingClientRect<HTMLDivElement>();

  const isOverflow = useIsOverflow(photoThumbnailsRef);

  return (
    <PhotoComparisonContainer>
      {profileImageUrl && (
        <PhotoComparisonHeader>
          <PhotoComparisonHeading size='sm' color='inverse'>
            Similarity level:
          </PhotoComparisonHeading>
          {currentReferenceImage['similarity-level'] ? (
            <Sticker variant='primary'>{getSimilarityLabel(currentReferenceImage)}</Sticker>
          ) : (
            <>
              <PhotoComparisonHeading size='sm' color='inverse'>
                N/A
              </PhotoComparisonHeading>
              <Tooltip
                side='top'
                iconTrigger={<StyledCircleHelpIcon />}
                zIndex={Z_INDICES.layeredDropdown}
              >
                <TooltipWrapper>
                  A similarity level is not available for this Report run. It may be due to the
                  quality of the end user&rsquo;s portrait.
                </TooltipWrapper>
              </Tooltip>
            </>
          )}
        </PhotoComparisonHeader>
      )}

      <ReferencePhotoContainer fullWidth={!profileImageUrl}>
        <StyledAsyncImage
          value={
            currentReferenceImage['internal-url']
              ? [{ url: currentReferenceImage['internal-url'] }]
              : undefined
          }
          allowPreviewZoom={false}
          width='100%'
          height='200px'
        />
        <PhotoLabel size='sm' color='inverse'>
          Source:{' '}
          <StyledLink href={currentReferenceImage['internal-url']} target='_blank' rel='noreferrer'>
            {/* TODO: remove this once non-wikidata urls are also pulled for pep */}
            Wikidata
          </StyledLink>
        </PhotoLabel>
      </ReferencePhotoContainer>

      {profileImageUrl && (
        <InputPhotoContainer>
          <StyledAsyncImage
            value={[{ url: profileImageUrl }]}
            width='100%'
            height='200px'
            allowPreviewZoom={false}
            modalLabel='Photo'
          />
          <PhotoLabel size='sm' color='inverse'>
            Input
          </PhotoLabel>
        </InputPhotoContainer>
      )}

      <NavContainer>
        {isOverflow && (
          <NavItems scrollRef={photoThumbnailsRef} boundingRect={photoThumbnailsBoundingRect} />
        )}
        {referenceImages.length > 1 && (
          <PhotoThumbnails ref={photoThumbnailsRef}>
            {referenceImages.map((referenceImage, idx) => {
              const isSelected = idx === imageIndex;

              return (
                <ThumbnailPhotoContainer
                  key={`pep-image__thumbnail-${idx}`}
                  id={`pep-image__thumbnail-${idx}`}
                  onClick={() => setImageIndex(idx)}
                  isSelected={isSelected}
                >
                  <StyledAsyncImage
                    value={
                      referenceImage['internal-url']
                        ? [{ url: referenceImage['internal-url'] }]
                        : undefined
                    }
                    allowPreviewZoom={false}
                    width={`${PHOTO_THUMBNAIL_WIDTH}px`}
                    height={`${PHOTO_THUMBNAIL_WIDTH}px`}
                  />
                  {profileImageUrl && (
                    <SimilaritySticker variant={isSelected ? 'primary' : 'basic'}>
                      {getSimilarityLabel(referenceImage)}
                    </SimilaritySticker>
                  )}
                </ThumbnailPhotoContainer>
              );
            })}
          </PhotoThumbnails>
        )}
      </NavContainer>
    </PhotoComparisonContainer>
  );
};

const getSimilarityLabel = (image: PoliticallyExposedPersonImage) => {
  switch (image?.['similarity-level']) {
    case 'low':
      return 'Low';
    case 'medium':
      return 'Medium';
    case 'high':
      return 'High';
    default:
      return 'N/A';
  }
};

const NavItems = ({
  scrollRef,
  boundingRect,
}: {
  scrollRef: RefObject<HTMLDivElement>;
  boundingRect: DOMRect | null;
}) => {
  const scrollRight = () => {
    const ref = scrollRef.current;
    if (!ref) {
      return;
    }
    const offset = ref.clientWidth - PHOTO_THUMBNAIL_WIDTH;
    ref.scrollBy({
      left: offset,
      behavior: 'smooth',
    });
  };

  const scrollLeft = () => {
    const ref = scrollRef.current;
    if (!ref) {
      return;
    }
    const offset = -(ref.clientWidth - PHOTO_THUMBNAIL_WIDTH);
    ref.scrollBy({
      left: offset,
      behavior: 'smooth',
    });
  };

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useEffect(() => {
    const ref = scrollRef.current;

    if (ref == null) {
      return;
    }

    const showLeft = ref.scrollLeft > 0;
    const showRight = ref.scrollWidth - ref.scrollLeft > ref.clientWidth;
    setShowLeft(showLeft);
    setShowRight(showRight);
  }, [scrollRef, boundingRect]);

  return (
    <>
      {showRight && (
        <NavButtonContainer dir='right'>
          <Button id='pep-image__next' onClick={scrollRight} width='icon' variant='inverse'>
            <ChevronDownIcon />
          </Button>
        </NavButtonContainer>
      )}
      {showLeft && (
        <NavButtonContainer dir='left'>
          <Button id='pep-image__left' onClick={scrollLeft} width='icon' variant='inverse'>
            <ChevronDownIcon />
          </Button>
        </NavButtonContainer>
      )}
    </>
  );
};

const PhotoComparisonContainer = styled.div`
  display: grid;
  column-count: 2;
  grid-template-columns: 1fr 1fr;
  background: ${Color.PrimaryBlack};
  border-radius: ${BORDER_RADIUSES.rounded};
  max-width: 800px;
`;

const NavButtonContainer = styled.div<{ dir: 'left' | 'right' }>`
  position: absolute;
  height: 100%;
  display: flex;
  align-items: center;
  bottom: 0;
  z-index: ${Z_INDICES.dropdown};
  ${({ dir }) =>
    dir === 'left'
      ? css`
          left: ${Spacing[1]};
          svg {
            transform: rotate(90deg);
          }
        `
      : css`
          right: ${Spacing[1]};
          svg {
            transform: rotate(270deg);
          }
        `}
  & > button {
    background: ${Color.PrimaryBlack};
    color: ${Color.PrimaryWhite};

    &:disabled {
      background: ${Color.PrimaryBlack};
      color: ${Color.TextDisabled};
    }
  }
`;

const TooltipWrapper = styled(Paragraph)`
  width: 300px;
`;

const PhotoComparisonHeader = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing[1]};
  border-bottom: 2px solid ${Color.CoreSlate900};
  height: 48px;
`;

const StyledAsyncImage = styled(AsyncImage)`
  & > div {
    border-radius: 0;

    & > img {
      background: ${Color.PrimaryBlack};
    }
  }
`;

const GRID_BORDER = `2px solid ${Color.CoreSlate900}`;

const PHOTO_THUMBNAIL_WIDTH = 84;

const SimilaritySticker = styled(Sticker)`
  position: absolute;
  top: ${Spacing[1]};
  left: ${Spacing[1]};
  z-index: ${Z_INDICES.tooltip};
`;

const PhotoContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: ${GRID_BORDER};
  padding: ${Spacing[2]};
  gap: ${Spacing[1]};
`;

const ThumbnailPhotoContainer = styled(PhotoContainer)<{ isSelected: boolean }>`
  position: relative;
  padding: 0;
  border-bottom: 0;

  ${({ isSelected }) =>
    isSelected &&
    css`
      outline: 2px solid ${Color.CorePurple600};
    `}

  &:hover {
    cursor: pointer;
  }
`;

const ReferencePhotoContainer = styled(PhotoContainer)<{ fullWidth: boolean }>`
  grid-column: ${({ fullWidth }) => (fullWidth ? '1 / -1' : '1')};
`;

const InputPhotoContainer = styled(PhotoContainer)`
  border-left: ${GRID_BORDER};
  border-bottom: ${GRID_BORDER};
`;

const PhotoLabel = styled(Paragraph)`
  align-self: center;
`;

const StyledLink = styled.a`
  text-decoration: underline;
  color: ${Color.PrimaryWhite};
`;

const PhotoComparisonHeading = styled(Paragraph)`
  font-weight: ${FontWeight.Medium};
`;

const PhotoThumbnails = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width: 100%;
  overflow: scroll;
  padding: ${Spacing[2]};
  gap: ${Spacing[2]};

  ::-webkit-scrollbar {
    display: none;
  }

  & img {
    object-fit: cover;
  }
`;

const StyledCircleHelpIcon = styled(CircleHelpIcon)`
  color: ${Color.PrimaryWhite};
`;

const NavContainer = styled.div`
  position: relative;
  grid-column: 1 / -1;
  overflow: hidden;
`;

export default PoliticallyExposedPersonPhotoComparison;
